import { Bounds } from 'pixi.js';
import { Signal } from 'typed-signals';
import type { Popup } from '../../../../popups/Popup';
import { DioramaSharePopupLayout } from '../../../../../layouts/popups/DioramaSharePopupLayout';
import { DioramaView } from '../../dioramas/DioramaView';
import type { App } from '../../../../app/App';
import type { DioramaConfig } from '../../../../app/state/diorama/dioramaState';
import { centerElements } from '../../../utils/centerElements';
import type { PopupElementAnimationData } from '../../../../popups/PopupAnimations';
import { soundManager } from '../../../../app/SoundManager';

export class DioramaSharePopup implements Popup {
  public static readonly requiredLayouts = [DioramaSharePopupLayout];

  private readonly layout = new DioramaSharePopupLayout();

  public readonly root = this.layout.root;

  public readonly initialBounds = new Bounds(0, 0, 780, 1280);

  public readonly onEvent = new Signal<(event: string) => void>();

  constructor(private readonly app: App) {
    const {
      shareButton: { root: shareButton },
      tapToContinueButton,
      rays,
      shareButtonIcon,
      shareButtonLabel,
    } = this.layout;

    shareButton.onpointerdown = async (): Promise<void> => {
      const shareImage = await this.getShareImage();
      this.onEvent.emit(shareImage);
    };

    rays.tweenTo({ angle: 360 }, 20000, 'linear', true, 0, -1);

    tapToContinueButton.onpointerdown = (): void => {
      this.onEvent.emit('finished');
    };

    centerElements([shareButtonIcon, shareButtonLabel], 10);
  }

  public onAdded(dioramaConfig: DioramaConfig): void {
    const dioramaView = new DioramaView({
      config: dioramaConfig,
      progress: Infinity,
      isLocked: false,
      hideRays: true,
      hideBackground: true
    });
    this.layout.dioramaSlot.addChild(dioramaView);

    this.root.tweenFrom({ alpha: 0 }, 300, 'sinOut', true);

    soundManager.play('UI_Diorama_Completed_ShareScreen');
  }

  private getShareImage(): Promise<string> {
    return this.app.renderer.pixi.renderer.extract.base64(this.layout.shareContainer);
  }

  public getAnimatedElements(phase: 'in' | 'out'): PopupElementAnimationData[] {
    const { layout } = this;

    if (phase === 'in') {
      return [{ container: layout.popup }];
    }

    return [
      { container: layout.contentContainer },
      { container: layout.shareButton.root, delay: 50 },
      { container: layout.tapToContinueButton, duration: 50 },
    ];
  }
}
